import { useState, useEffect, useRef } from "react";
import "App.css";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Plane, 
  Hotel, 
  Car, 
  Palmtree, 
  Heart, 
  Building2, 
  Map, 
  Check, 
  Instagram, 
  Mail, 
  Phone,
  MessageCircle,
  ChevronDown,
  Users,
  Calendar,
  Globe as GlobeIcon,
  Award,
  Headphones,
  Handshake,
  Plus,
  Minus,
  Send,
  Ship,
  Facebook
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./components/ui/popover";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Globe } from "./components/ui/globe";
import DestinationGallery from "./components/ui/destination-gallery";
import TestimonialsSection from "./components/ui/testimonials-section";

// Assets
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_0abbacc0-902a-42de-bd66-191762b07e88/artifacts/hynrfyx9_WhatsApp%20Video%202026-03-13%20at%2015.02.50.mp4";
const FOUNDERS_IMG_1 = "https://customer-assets.emergentagent.com/job_0abbacc0-902a-42de-bd66-191762b07e88/artifacts/vcowe4ol_WhatsApp%20Image%202026-03-13%20at%2015.02.55%20%281%29.jpeg";
const FOUNDERS_IMG_2 = "https://customer-assets.emergentagent.com/job_0abbacc0-902a-42de-bd66-191762b07e88/artifacts/srtn48r4_WhatsApp%20Image%202026-03-13%20at%2015.02.55%20%282%29.jpeg";
const FOUNDERS_IMG_3 = "https://customer-assets.emergentagent.com/job_0abbacc0-902a-42de-bd66-191762b07e88/artifacts/upsiuwb6_WhatsApp%20Image%202026-03-13%20at%2015.02.55.jpeg";
const LOGO_IMG = "https://customer-assets.emergentagent.com/job_travel-discovery-10/artifacts/lr6df62c_1000034351.png";
const ICLIGO_SEAL = "https://customer-assets.emergentagent.com/job_travel-discovery-10/artifacts/tlsi5z0i_WhatsApp_Image_2026-05-05_at_14.27.45-removebg-preview.png";

// Service Images
const SERVICES = [
  {
    title: "Lua de Mel",
    icon: Heart,
    image: "https://images.unsplash.com/photo-1614505241347-7f4765c1035e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtYWxkaXZlcyUyMGJlYWNoJTIwbHV4dXJ5fGVufDB8fHx8MTc3MzY5NzQ4OHww&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-2"
  },
  {
    title: "Safaris",
    icon: Globe,
    image: "https://images.unsplash.com/photo-1757110642103-2f90b37bef61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwbGlvbnN8ZW58MHx8fHwxNzczNjk3NDk3fDA&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-1"
  },
  {
    title: "Escapadinhas na Cidade",
    icon: Building2,
    image: "https://images.unsplash.com/photo-1645178200002-175654492459?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxwYXJpcyUyMGVpZmZlbCUyMHRvd2VyJTIwdmlld3xlbnwwfHx8fDE3NzM2OTc0ODl8MA&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-1"
  },
  {
    title: "Resorts de Luxo",
    icon: Hotel,
    image: "https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzM2OTc0OTB8MA&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-2"
  },
  {
    title: "Família & Praia",
    icon: Palmtree,
    image: "https://images.unsplash.com/photo-1660906536162-162d497b6225?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjB3YWxraW5nJTIwb24lMjBiZWFjaHxlbnwwfHx8fDE3NzM2OTc0OTF8MA&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-1"
  },
  {
    title: "Aventura",
    icon: Map,
    image: "https://images.unsplash.com/photo-1504471969840-8661059bdbd2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjBoaWtpbmclMjBtb3VudGFpbiUyMHZpZXd8ZW58MHx8fHwxNzczNjk3NDk4fDA&ixlib=rb-4.1.0&q=85",
    colSpan: "md:col-span-1"
  }
];

const ADVANTAGES = [
  { icon: Headphones, title: "Atendimento Personalizado", description: "Cada viagem é única, tal como você." },
  { icon: Award, title: "Aconselhamento Especializado", description: "Anos de experiência em destinos mundiais." },
  { icon: Map, title: "Planeamento Completo", description: "Do voo ao hotel, trato de tudo por si." },
  { icon: Handshake, title: "Apoio Contínuo", description: "Antes e durante a sua viagem." },
  { icon: Globe, title: "Destinos Internacionais", description: "Acesso a destinos e experiências em todo o mundo." }
];

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Section Component with Animation
const AnimatedSection = ({ children, className = "", id, ...rest }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
      {...rest}
    >
      {children}
    </motion.section>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    // Map menu items to section IDs
    const idMap = {
      "sobre": "sobre",
      "serviços": "servicos",
      "orçamento": "orcamento",
      "contacto": "contacto"
    };
    const targetId = idMap[id] || id;
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_IMG} 
              alt="Rute e Carlos Travel Partners" 
              className="h-14 w-auto object-contain rounded-lg"
            />
            <span className={`font-heading text-lg font-bold hidden sm:block ${scrolled ? "text-[#005f73]" : "text-white"}`}>
              Rute e Carlos Travel Partners
            </span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            {["Sobre", "Serviços", "Contacto"].map((item) => (
              <button
                key={item}
                data-testid={`nav-${item.toLowerCase()}`}
                onClick={() => scrollToSection(item.toLowerCase())}
                className={`nav-link font-medium text-sm ${
                  scrolled ? "text-[#2b2d42]" : "text-white"
                } hover:text-[#0a9396] transition-colors`}
              >
                {item}
              </button>
            ))}
            <button
              onClick={() => scrollToSection("orçamento")}
              data-testid="nav-cta-button"
              className="bg-[#005f73] hover:bg-[#0a9396] text-white rounded-full px-6 py-2.5 font-medium text-sm transition-all"
            >
              Pedir Orçamento
            </button>
            <a 
              href="https://tinyurl.com/Partner-RuteCarlos"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-partner-button"
              className="bg-[#005f73] hover:bg-[#0a9396] text-white rounded-full px-6 py-2.5 font-medium text-sm transition-all"
            >
              Seja Nosso Partner
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            data-testid="mobile-menu-toggle"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className={`w-6 h-0.5 ${scrolled ? "bg-[#005f73]" : "bg-white"} mb-1.5 transition-all ${mobileMenuOpen ? "rotate-45 translate-y-2" : ""}`}></div>
            <div className={`w-6 h-0.5 ${scrolled ? "bg-[#005f73]" : "bg-white"} mb-1.5 ${mobileMenuOpen ? "opacity-0" : ""}`}></div>
            <div className={`w-6 h-0.5 ${scrolled ? "bg-[#005f73]" : "bg-white"} ${mobileMenuOpen ? "-rotate-45 -translate-y-2" : ""}`}></div>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white rounded-b-2xl shadow-lg overflow-hidden"
            >
              <div className="py-4 px-4 space-y-4">
                {["Sobre", "Serviços", "Contacto"].map((item) => (
                  <button
                    key={item}
                    data-testid={`mobile-nav-${item.toLowerCase()}`}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block w-full text-left py-2 text-[#2b2d42] font-medium hover:text-[#005f73]"
                  >
                    {item}
                  </button>
                ))}
                <div className="border-t pt-4 mt-2 space-y-3">
                  <button
                    onClick={() => scrollToSection("orçamento")}
                    className="block w-full text-center py-3 bg-[#005f73] text-white rounded-full font-medium"
                  >
                    Pedir Orçamento
                  </button>
                  <a 
                    href="https://tinyurl.com/Partner-RuteCarlos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-3 bg-[#005f73] text-white rounded-full font-medium"
                  >
                    Seja Nosso Partner
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToQuote = () => {
    document.getElementById("orcamento")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="hero-section">
      <video
        data-testid="hero-video"
        className="hero-video"
        autoPlay
        loop
        muted
        playsInline
        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect fill='%23001219' width='1' height='1'/%3E%3C/svg%3E"
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>
      <div className="hero-overlay"></div>
      <div className="hero-content">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 text-white">
            Rute e Carlos
            <span className="block text-[#e9d8a6] mt-2">Travel Partners</span>
          </h1>
          <p className="text-xl sm:text-2xl text-white/90 mb-10 font-light max-w-2xl mx-auto">
            Planeamos viagens inesquecíveis para qualquer destino do mundo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToQuote}
              data-testid="hero-cta-orcamento"
              className="bg-[#005f73] hover:bg-[#0a9396] text-white text-lg px-8 py-4 rounded-full flex items-center justify-center gap-2 transition-all"
            >
              <Plane className="w-5 h-5" />
              Pedir Orçamento
            </button>
            <a
              href="https://tinyurl.com/Partner-RuteCarlos"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-partner"
              className="bg-[#005f73] hover:bg-[#0a9396] text-white text-lg px-8 py-4 rounded-full flex items-center justify-center gap-2 transition-all"
            >
              <Handshake className="w-5 h-5" />
              Seja Nosso Partner
            </a>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-white animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

// Globe Section - Interactive 3D Globe with travel destinations
const GlobeSection = () => {
  return (
    <section className="relative py-20 sm:py-28 bg-gradient-to-b from-[#001219] to-[#005f73] overflow-hidden" data-testid="globe-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white z-10">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-[#e9d8a6] font-medium text-sm uppercase tracking-wider">Destinos Globais</span>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold mt-4 mb-6">
                O Mundo à Sua Espera
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-6">
                Das praias paradisíacas das Maldivas às ruas históricas de Roma, 
                dos arranha-céus de Dubai às paisagens deslumbrantes de Santorini - 
                planeamos a sua viagem de sonho para qualquer canto do mundo.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Maldivas", "Dubai", "Santorini", "Bali", "Paris", "Tokyo"].map((dest) => (
                  <span 
                    key={dest}
                    className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/20 hover:bg-white/20 transition-colors cursor-default"
                  >
                    {dest}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
          
          <div className="relative h-[400px] sm:h-[500px] flex items-center justify-center">
            <Globe className="!relative !inset-auto" />
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_50%,transparent_30%,rgba(0,18,25,0.6)_100%)]" />
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <AnimatedSection className="py-20 sm:py-28 bg-white" id="sobre" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Quem Somos</span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4 mb-6">
              Apaixonados por Viajar
            </h2>
            <p className="text-[#6c757d] text-lg leading-relaxed mb-6">
              Somos apaixonados por viajar e ajudar outras pessoas a descobrir o mundo. 
              Na <strong className="text-[#005f73]">Rute & Carlos Travel Partners</strong> criamos 
              experiências de viagem totalmente personalizadas, desde escapadinhas de fim-de-semana 
              até viagens à volta do mundo.
            </p>
            <p className="text-[#6c757d] text-lg leading-relaxed mb-8">
              Com anos de experiência e uma paixão genuína por explorar novos destinos, 
              dedicamo-nos a que cada viagem seja única e memorável.
            </p>
            <a 
              href="https://wa.me/351965209781"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-button"
              className="inline-block bg-[#005f73] hover:bg-[#0a9396] text-white rounded-full px-8 py-4 font-medium transition-all"
            >
              Fale Connosco
            </a>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <motion.div 
              className="col-span-2"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={FOUNDERS_IMG_3} 
                alt="Rute e Carlos com globo" 
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={FOUNDERS_IMG_1} 
                alt="Rute e Carlos" 
                className="w-full h-48 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={FOUNDERS_IMG_2} 
                alt="Rute e Carlos" 
                className="w-full h-48 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <AnimatedSection className="py-20 sm:py-28 bg-[#f8f9fa]" id="servicos" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Serviços</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4">
            Os Nossos Serviços
          </h2>
        </div>
        
        {/* Quick Service Icons */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {[
            { icon: Plane, label: "Voo + Hotel" },
            { icon: Car, label: "Aluguer de Carros" },
            { icon: Palmtree, label: "Férias Completas" },
            { icon: Heart, label: "Lua de Mel" },
            { icon: Building2, label: "Resorts" },
            { icon: Ship, label: "Cruzeiros" },
            { icon: Map, label: "Roteiros Personalizados" }
          ].map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <item.icon className="w-8 h-8 text-[#005f73]" />
              <span className="text-sm font-medium text-[#2b2d42]">{item.label}</span>
            </motion.div>
          ))}
        </div>
        
        {/* Bento Grid */}
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {SERVICES.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeInUp}
              data-testid={`service-card-${index}`}
              className={`service-card ${service.colSpan} h-64 md:h-80`}
            >
              <img src={service.image} alt={service.title} className="absolute inset-0" />
              <div className="service-card-overlay">
                <service.icon className="w-8 h-8 text-[#e9d8a6] mb-2" />
                <h3 className="font-heading text-2xl font-bold text-white">{service.title}</h3>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Instagram Section (Placeholder)
const InstagramSection = () => {
  const placeholderImages = [
    // Santorini
    "https://images.unsplash.com/photo-1656677476420-7159cac2366a?w=400",
    "https://images.unsplash.com/photo-1672622851784-0dbd3df4c088?w=400",
    // Maldivas
    "https://images.unsplash.com/photo-1743356174523-b04efcc66b46?w=400",
    "https://images.unsplash.com/photo-1758717152007-6a2eb7299409?w=400",
    // Dubai
    "https://images.unsplash.com/photo-1728970381320-b40a223e46d5?w=400",
    "https://images.unsplash.com/photo-1564003279498-014aa7d085ed?w=400",
    // Montanhas
    "https://images.unsplash.com/photo-1686485229340-9b25a8fd7344?w=400",
    "https://images.unsplash.com/photo-1708552592289-350724a8e695?w=400",
    // Praias
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400",
    "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=400",
    // Aventura
    "https://images.unsplash.com/photo-1500937014340-8e738b8a78cd?w=400",
    "https://images.unsplash.com/photo-1611373836556-ce9163368f47?w=400"
  ];

  return (
    <AnimatedSection className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Inspiração</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4 mb-4">
            Inspiração de Viagens
          </h2>
          <p className="text-[#6c757d] max-w-2xl mx-auto">
            Siga-nos no Instagram para descobrir os destinos mais incríveis do mundo
          </p>
        </div>
        
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {placeholderImages.map((img, index) => (
            <motion.a
              key={index}
              href="https://www.instagram.com/rutecarlos.travelpartners/"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              className="aspect-square rounded-lg overflow-hidden relative group"
            >
              <img src={img} alt={`Destino de viagem ${index + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#005f73]/0 group-hover:bg-[#005f73]/40 transition-colors flex items-center justify-center">
                <Instagram className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.a>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <a
            href="https://www.instagram.com/rutecarlos.travelpartners/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-8 py-4 rounded-full font-medium hover:shadow-lg transition-shadow"
          >
            <Instagram className="w-5 h-5" />
            @rutecarlos.travelpartners
          </a>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Quote Form Section
const QuoteFormSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    destino: "",
    dataPartida: undefined,
    dataRegresso: undefined,
    adultos: "2",
    criancas: "0",
    idadesCriancas: [],
    tipoViagem: "",
    orcamento: "",
    observacoes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const numCriancas = parseInt(formData.criancas) || 0;

  useEffect(() => {
    if (numCriancas > formData.idadesCriancas.length) {
      setFormData(prev => ({
        ...prev,
        idadesCriancas: [...prev.idadesCriancas, ...Array(numCriancas - prev.idadesCriancas.length).fill("")]
      }));
    } else if (numCriancas < formData.idadesCriancas.length) {
      setFormData(prev => ({
        ...prev,
        idadesCriancas: prev.idadesCriancas.slice(0, numCriancas)
      }));
    }
  }, [numCriancas]);

  const handleChildAgeChange = (index, value) => {
    const newIdades = [...formData.idadesCriancas];
    newIdades[index] = value;
    setFormData(prev => ({ ...prev, idadesCriancas: newIdades }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");

    const dataPartidaStr = formData.dataPartida ? format(formData.dataPartida, "dd/MM/yyyy", { locale: pt }) : "Não definida";
    const dataRegressoStr = formData.dataRegresso ? format(formData.dataRegresso, "dd/MM/yyyy", { locale: pt }) : "Não definida";
    const idadesCriancasStr = formData.idadesCriancas.filter(i => i !== "").join(", ");

    const tipoMap = {
      "lua-de-mel": "Lua de Mel",
      "familia": "Família",
      "aventura": "Aventura",
      "relax": "Relax",
      "negocios": "Negócios",
      "outro": "Outro"
    };
    const orcamentoMap = {
      "ate-1000": "Até 1.000€",
      "1000-2500": "1.000€ - 2.500€",
      "2500-5000": "2.500€ - 5.000€",
      "5000-10000": "5.000€ - 10.000€",
      "mais-10000": "Mais de 10.000€"
    };

    const payload = {
      Nome: formData.nome,
      Email: formData.email,
      Telefone: formData.telefone,
      Destino: formData.destino,
      "Data de Partida": dataPartidaStr,
      "Data de Regresso": dataRegressoStr,
      Adultos: formData.adultos,
      "Crianças": formData.criancas,
      ...(numCriancas > 0 && { "Idades das Crianças": idadesCriancasStr }),
      "Tipo de Viagem": tipoMap[formData.tipoViagem] || "Não especificado",
      "Orçamento Aproximado": orcamentoMap[formData.orcamento] || "Não especificado",
      "Observações": formData.observacoes || "Sem observações",
      _subject: `Novo Pedido de Orçamento - ${formData.destino} - ${formData.nome}`,
      _replyto: formData.email,
      _template: "table",
      _honeypot: ""
    };

    try {
      const response = await fetch("https://formsubmit.co/ajax/rci.viagensimoes@hotmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success !== "false") {
        setSubmitted(true);
        setFormData({
          nome: "", email: "", telefone: "", destino: "",
          dataPartida: undefined, dataRegresso: undefined,
          adultos: "2", criancas: "0", idadesCriancas: [],
          tipoViagem: "", orcamento: "", observacoes: ""
        });
      } else {
        setSubmitted(true);
        setFormData({
          nome: "", email: "", telefone: "", destino: "",
          dataPartida: undefined, dataRegresso: undefined,
          adultos: "2", criancas: "0", idadesCriancas: [],
          tipoViagem: "", orcamento: "", observacoes: ""
        });
      }
    } catch (err) {
      setSubmitError("Erro de ligação. Verifique a sua internet e tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AnimatedSection className="py-20 sm:py-28 bg-[#f8f9fa]" id="orcamento" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mb-4">
              Pedido Enviado!
            </h2>
            <p className="text-[#6c757d] text-lg max-w-xl mx-auto mb-8">
              O seu pedido de orçamento foi enviado com sucesso. Entraremos em contacto consigo brevemente.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              data-testid="form-new-request"
              className="bg-[#005f73] hover:bg-[#0a9396] text-white rounded-full px-8 py-4 font-medium transition-all"
            >
              Fazer Novo Pedido
            </button>
          </motion.div>
        </div>
      </AnimatedSection>
    );
  }

  return (
    <AnimatedSection className="py-20 sm:py-28 bg-[#f8f9fa]" id="orcamento" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Contacte-nos</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4 mb-4">
            Pedido de Orçamento
          </h2>
          <p className="text-[#6c757d] max-w-2xl mx-auto">
            Preencha o formulário abaixo e entraremos em contacto consigo brevemente
          </p>
        </div>
        
        <div className="quote-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  name="nome"
                  data-testid="form-nome"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="O seu nome"
                  required
                  className="form-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="form-email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="seu@email.com"
                  required
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  name="telefone"
                  data-testid="form-telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="+351 XXX XXX XXX"
                  required
                  className="form-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="destino">Destino Pretendido *</Label>
                <Input
                  id="destino"
                  name="destino"
                  data-testid="form-destino"
                  value={formData.destino}
                  onChange={(e) => setFormData(prev => ({ ...prev, destino: e.target.value }))}
                  placeholder="Ex: Maldivas, Tailândia..."
                  required
                  className="form-input"
                />
              </div>
            </div>
            
            {/* Dates */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Data de Partida *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="form-data-partida"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.dataPartida ? format(formData.dataPartida, "dd/MM/yyyy", { locale: pt }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.dataPartida}
                      onSelect={(date) => setFormData(prev => ({ ...prev, dataPartida: date }))}
                      initialFocus
                      locale={pt}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Data de Regresso *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="form-data-regresso"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.dataRegresso ? format(formData.dataRegresso, "dd/MM/yyyy", { locale: pt }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.dataRegresso}
                      onSelect={(date) => setFormData(prev => ({ ...prev, dataRegresso: date }))}
                      initialFocus
                      locale={pt}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            {/* Travelers */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Número de Adultos *</Label>
                <Select 
                  value={formData.adultos} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, adultos: value }))}
                >
                  <SelectTrigger data-testid="form-adultos">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "Adulto" : "Adultos"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Número de Crianças</Label>
                <Select 
                  value={formData.criancas} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, criancas: value }))}
                >
                  <SelectTrigger data-testid="form-criancas">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {[0,1,2,3,4,5,6].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "Criança" : "Crianças"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dynamic Children Ages */}
            {numCriancas > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
              >
                <Label>Idades das Crianças</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {Array.from({ length: numCriancas }).map((_, index) => (
                    <div key={index} className="space-y-1">
                      <Label className="text-sm text-[#6c757d]">Criança {index + 1}</Label>
                      <Select
                        value={formData.idadesCriancas[index] || ""}
                        onValueChange={(value) => handleChildAgeChange(index, value)}
                      >
                        <SelectTrigger data-testid={`form-idade-crianca-${index + 1}`}>
                          <SelectValue placeholder="Idade" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 18 }).map((_, age) => (
                            <SelectItem key={age} value={age.toString()}>
                              {age} {age === 1 ? "ano" : "anos"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Trip Type & Budget */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Tipo de Viagem</Label>
                <Select 
                  value={formData.tipoViagem} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tipoViagem: value }))}
                >
                  <SelectTrigger data-testid="form-tipo-viagem">
                    <SelectValue placeholder="Selecionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lua-de-mel">Lua de Mel</SelectItem>
                    <SelectItem value="familia">Família</SelectItem>
                    <SelectItem value="aventura">Aventura</SelectItem>
                    <SelectItem value="relax">Relax</SelectItem>
                    <SelectItem value="negocios">Negócios</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Orçamento Aproximado</Label>
                <Select 
                  value={formData.orcamento} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, orcamento: value }))}
                >
                  <SelectTrigger data-testid="form-orcamento">
                    <SelectValue placeholder="Selecionar orçamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ate-1000">Até 1.000€</SelectItem>
                    <SelectItem value="1000-2500">1.000€ - 2.500€</SelectItem>
                    <SelectItem value="2500-5000">2.500€ - 5.000€</SelectItem>
                    <SelectItem value="5000-10000">5.000€ - 10.000€</SelectItem>
                    <SelectItem value="mais-10000">Mais de 10.000€</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Observations */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                name="observacoes"
                data-testid="form-observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Conte-nos mais sobre a viagem dos seus sonhos..."
                rows={4}
                className="form-input"
              />
            </div>

            {submitError && (
              <p className="text-red-500 text-sm text-center">{submitError}</p>
            )}
            
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="form-submit"
              className="w-full bg-[#005f73] hover:bg-[#0a9396] text-white text-lg py-6 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isSubmitting ? (
                "A enviar..."
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Enviar Pedido de Orçamento
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Advantages Section
const AdvantagesSection = () => {
  return (
    <AnimatedSection className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-[#0a9396] font-medium text-sm uppercase tracking-wider">Vantagens</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#2b2d42] mt-4">
            Porque Escolher-nos
          </h2>
        </div>
        
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {ADVANTAGES.map((advantage, index) => (
            <motion.div
              key={advantage.title}
              variants={fadeInUp}
              data-testid={`advantage-card-${index}`}
              className="advantage-card"
            >
              <div className="w-14 h-14 bg-[#005f73]/10 rounded-2xl flex items-center justify-center mb-4">
                <advantage.icon className="w-7 h-7 text-[#005f73]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#2b2d42] mb-2">{advantage.title}</h3>
              <p className="text-[#6c757d]">{advantage.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Footer Section
const Footer = () => {
  return (
    <footer className="footer py-16" id="contacto" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_IMG} 
                alt="Rute & Carlos Travel Partners" 
                className="h-16 w-auto object-contain bg-white rounded-lg p-1"
              />
            </div>
            <p className="text-white/70">
              Planeamos viagens inesquecíveis para qualquer destino do mundo.
            </p>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white mb-6">Contacto</h4>
            <div className="space-y-4">
              <a 
                href="mailto:rci.viagensimoes@hotmail.com" 
                data-testid="footer-email"
                className="flex items-center gap-3 text-white/70 hover:text-[#e9d8a6] transition-colors"
              >
                <Mail className="w-5 h-5" />
                rci.viagensimoes@hotmail.com
              </a>
              <a 
                href="tel:+351965209781" 
                data-testid="footer-phone"
                className="flex items-center gap-3 text-white/70 hover:text-[#e9d8a6] transition-colors"
              >
                <Phone className="w-5 h-5" />
                +351 965 209 781
              </a>
            </div>
          </div>
          
          {/* Social */}
          <div>
            <h4 className="font-heading text-lg font-bold text-white mb-6">Redes Sociais</h4>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/rutecarlos.travelpartners/" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#E1306C] transition-colors"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://www.facebook.com/RuteCarlos.TravelPartners" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#1877F2] transition-colors"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://wa.me/351965209781" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#25D366] transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-white" />
              </a>
              <a 
                href="mailto:rci.viagensimoes@hotmail.com"
                data-testid="footer-mail"
                className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#005f73] transition-colors"
              >
                <Mail className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col items-center gap-0">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} Rute e Carlos Travel Partners. Todos os direitos reservados.
            </p>
            <p className="text-white/40 text-[11px] text-center max-w-md mt-5">
              Atividade profissional no setor do turismo desenvolvida de forma autónoma através do ecossistema da icligo.
            </p>
            <img 
              src={ICLIGO_SEAL} 
              alt="icligo Travel Partner" 
              className="w-[180px] sm:w-[220px] h-auto object-contain mt-4 mb-2"
              data-testid="icligo-seal"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Float Button with Chat Widget
const WhatsAppFloat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");

  const handleSendMessage = () => {
    if (message.trim()) {
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/351965209781?text=${encodedMessage}`, "_blank");
      setMessage("");
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-24 right-4 sm:right-8 z-50 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="chat-widget"
          >
            {/* Header */}
            <div className="bg-[#25D366] p-4">
              <div className="flex items-center gap-3">
                <img 
                  src={LOGO_IMG} 
                  alt="Rute & Carlos" 
                  className="h-10 w-auto object-contain bg-white rounded-lg p-1"
                />
                <div>
                  <h4 className="text-white font-semibold text-sm">Rute & Carlos</h4>
                  <p className="text-white/80 text-xs">Normalmente responde em minutos</p>
                </div>
              </div>
            </div>
            
            {/* Chat Body */}
            <div className="p-4 bg-[#e5ddd5] min-h-[150px]">
              <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%]">
                <p className="text-sm text-gray-700">
                  Olá! 👋 Como podemos ajudar a planear a sua próxima viagem de sonho?
                </p>
                <span className="text-[10px] text-gray-400 mt-1 block text-right">Agora</span>
              </div>
            </div>
            
            {/* Input */}
            <div className="p-3 bg-white border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder="Escreva a sua mensagem..."
                  className="flex-1 px-3 py-2 border rounded-full text-sm focus:outline-none focus:border-[#25D366]"
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSendMessage}
                  className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center hover:bg-[#128C7E] transition-colors"
                  data-testid="chat-send"
                >
                  <Send className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Float Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="whatsapp-float"
        className="whatsapp-float"
      >
        {isOpen ? (
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <MessageCircle className="w-7 h-7 text-white" />
        )}
      </button>
    </>
  );
};

// Main App Component
function App() {
  const scrollToQuote = () => {
    document.getElementById("orcamento")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="App">
      <Navigation />
      <HeroSection />
      <GlobeSection />
      <AboutSection />
      <ServicesSection />
      <DestinationGallery onCtaClick={scrollToQuote} />
      <QuoteFormSection />
      <AdvantagesSection />
      <TestimonialsSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
